import type {
  ContentItem,
  ContentPage,
  ContentSection,
  ContentText,
  DrupalPath,
  DrupalUriLink,
} from '#/api/generated/fINRAMarketDataAPI.schemas';

/**
 * Shared primitives for the per-document content modules (homepage, etc).
 */

/**
 * Unwrap a Drupal ContentText to a plain string.
 */
export function contentText(
  text: ContentText | null | undefined,
): string | null {
  return text?.value ?? text?.summary ?? null;
}

/**
 * Unwrap a Drupal ContentText for rendering as a React text node.
 *
 * Drupal rich-text fields wrap even single paragraphs in markup
 * (`<p>...</p>`), so tags are stripped and whitespace collapsed. Safe by
 * construction: the result is rendered as text, never injected as HTML.
 */
export function contentTextPlain(
  text: ContentText | null | undefined,
): string | null {
  const raw = contentText(text);
  if (!raw) {
    return null;
  }

  const stripped = raw
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return stripped.length > 0 ? stripped : null;
}

/**
 * Editorial content domain.
 */
export const FINRA_CONTENT_ORIGIN = 'https://www.finra.org';

/**
 * Qualify a Drupal content href to an absolute URL.
 */
export function qualifyContentHref(href: string): string {
  return href.startsWith('/') ? `${FINRA_CONTENT_ORIGIN}${href}` : href;
}

/**
 * Resolve a Drupal link-field URI to a renderable href. Drupal stores host-site
 * links as `internal:/path`; external links keep their scheme. Both are
 * qualified to an absolute URL. URIs that can't render as an href
 * (`entity:node/…`, `route:…`) drop out.
 */
export function drupalLinkHref(uri: string | null | undefined): string | null {
  const href = uri?.trim().replace(/^internal:/, '');
  if (!href) {
    return null;
  }

  if (!href.startsWith('/') && !/^https?:\/\//.test(href)) {
    return null;
  }

  return qualifyContentHref(href);
}

/** A renderable link: resolved href plus its own display text. */
export interface ContentLink {
  href: string;
  title: string;
}

/**
 * Normalize a Drupal link field into a renderable link, or `null` when it
 * can't render — the URI must resolve to an href (see `drupalLinkHref`) and
 * the field must carry its own display text.
 */
export function contentLink(
  link: DrupalUriLink | null | undefined,
): ContentLink | null {
  const href = drupalLinkHref(link?.uri);
  const title = link?.title?.trim();

  return href && title ? { href, title } : null;
}

/** A renderable article teaser card, shared across content documents. */
export interface ContentArticleCard {
  title: string;
  /** Teaser text; cards render without one when editors leave it empty. */
  summary: string | null;
  /** Absolute finra.org URL — a host-site article, not an SPA route. */
  href: string;
}

/**
 * Normalize a `node--article` content item into a teaser card, or `null` when
 * a render-critical field (title, link) is missing. Only the teaser in
 * `summary` belongs on a card — never the full body — and it is optional.
 */
export function contentArticleCard(
  item: ContentItem,
): ContentArticleCard | null {
  const title = item.title?.trim();
  const summary = contentTextPlain({ value: item.body?.summary });
  const alias = item.path?.alias?.trim();
  const href = alias ? qualifyContentHref(alias) : null;

  return title && href ? { title, summary, href } : null;
}

/**
 * Normalize a named section's article items into renderable teaser cards, or
 * `null` when no renderable card remains — items missing a render-critical
 * field are dropped, never padded with placeholders.
 */
export function contentArticleCards(
  page: ContentPage | null | undefined,
  key: string,
): ContentArticleCard[] | null {
  const items = contentSection(page, key)?.items ?? [];
  const cards = items.flatMap((item) => {
    const card = contentArticleCard(item);
    return card ? [card] : [];
  });

  return cards.length > 0 ? cards : null;
}

/** Find a named section (`sections[].key`) on a content page. */
export function contentSection(
  page: ContentPage | null | undefined,
  key: string,
): ContentSection | null {
  return page?.sections?.find((section) => section.key === key) ?? null;
}

/**
 * A glossary term reconciled from the two places Drupal serializes it on a
 * detail page: the flat `taxonomy_terms[]` (short `tooltip` + `expanded_definition`)
 * and the same entity nested under a stat section (its long `text` description).
 * Both are keyed by the term's path-alias slug, so one lookup drives the hover
 * tooltip, the inline stat description, and (later) the drawer.
 */
export interface DefinitionTerm {
  slug: string;
  name: string | null;
  /** Short one-liner for the hover popover. */
  tooltip: string | null;
  /** Long description shown inline under a stat, as plain text. */
  description: string | null;
  /** Rich-text drawer body; null until Drupal editors author it. */
  expandedDefinition: string | null;
}

/** The last non-empty segment of a Drupal path alias, used as a term slug. */
function pathSlug(path: DrupalPath | null | undefined): string | null {
  const segments = path?.alias?.split('/').filter(Boolean) ?? [];
  return segments.length > 0 ? segments[segments.length - 1]! : null;
}

/**
 * Build a slug → {@link DefinitionTerm} lookup for a detail page. The flat
 * `taxonomy_terms` carry the tooltip and (eventual) expanded definition; the
 * same terms nested in the stat sections carry the long description. They share
 * a stable `id` and path alias, so both halves fold into one entry per slug.
 * Returns an empty map when the page is missing — the total-failure state.
 */
export function normalizeDefinitions(
  page: ContentPage | null | undefined,
): Map<string, DefinitionTerm> {
  const terms = new Map<string, DefinitionTerm>();
  if (!page) {
    return terms;
  }

  for (const term of page.taxonomy_terms ?? []) {
    const slug = pathSlug(term.path);
    if (!slug) {
      continue;
    }
    terms.set(slug, {
      slug,
      name: term.name ?? null,
      tooltip: term.tooltip ?? null,
      description: null,
      expandedDefinition: contentText(term.expanded_definition),
    });
  }

  for (const section of page.sections ?? []) {
    for (const item of section.items ?? []) {
      if (!item.type.startsWith('taxonomy_term--')) {
        continue;
      }
      const slug = pathSlug(item.path);
      if (!slug) {
        continue;
      }
      const description = contentTextPlain(item.text);
      const existing = terms.get(slug);
      if (existing) {
        existing.description = description;
      } else {
        terms.set(slug, {
          slug,
          name: item.title ?? null,
          tooltip: null,
          description,
          expandedDefinition: null,
        });
      }
    }
  }

  return terms;
}

export function isContentPage(value: unknown): value is ContentPage {
  return (
    value !== null &&
    typeof value === 'object' &&
    'id' in value &&
    'type' in value &&
    'feature' in value
  );
}

/**
 * The subset of a view model that requires static fallback copy: exactly its
 * non-nullable fields, allowing tiers to be compile-enforced.
 */
export type StaticFallback<T> = {
  [K in keyof T as null extends T[K] ? never : K]: T[K];
};
